import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/layout';
import { useProfileManagement } from '../../components/auth/useProfileManagement';
import ResourceRelationships from '../../components/resources/ResourceRelationships';
import { ProfileData } from '../../types/auth';
import { API_ENDPOINTS } from '../../config/api.config';
import { AlertCircle } from 'lucide-react';

interface DataSourceItem {
    id: string;
    name: string;
    path?: string;
    type?: string;
    item_type?: string;
    source_type?: string;
    metadata?: {
        display_path?: string;
        file_count?: number;
        original_metadata?: {
            mime_type?: string;
            mimeType?: string;
            file_size?: number;
        };
        depth?: number;
        s3_prefix?: string;
        path_tokens?: string[];
    };
    file_path?: string;
    description?: string;
    status?: string;
    is_root?: boolean;
    folder_id?: string;
}

interface ResourceItem {
    id: string;
    name: string;
    data_source_ids?: string[]; // For indexes
    index_ids?: string[];      // For RAG applications
    status?: string;
}

interface ResourceData {
    dataSources: DataSourceItem[];
    indexes: ResourceItem[];
    ragApplications: ResourceItem[];
}

interface APIListResponse<T> {
    dataSources: T[];
    total?: number;
    page?: number;
    totalPages?: number;
    limit?: number;
}

const ResourceRelationshipsPage: React.FC = () => {
    const { user } = useAuth();
    const modalState = useModal();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<ProfileData>({
        fullName: user?.fullName || '',
        email: user?.email || ''
    });

    const [resourceData, setResourceData] = useState<ResourceData>({
        dataSources: [],
        indexes: [],
        ragApplications: []
    });

    const profileManagement = useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    const fetchAllData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // Get files for each folder recursively with path
            const getAllFiles = async (folderId: string, parentPath: string = ''): Promise<DataSourceItem[]> => {
                console.log(`\nFetching contents of folder ${folderId}`);
                const filesRes = await fetch(
                    `${API_ENDPOINTS.DATA_SOURCE.LIST}?folder_id=${folderId}`,
                    { headers }
                );
                const filesData: APIListResponse<DataSourceItem> = await filesRes.json();
                console.log(`Files response for folder ${folderId}:`, filesData);

                let allFiles: DataSourceItem[] = [];

                // Process all items in this folder
                for (const item of filesData.dataSources || []) {
                    console.log(`Processing item in folder ${folderId}:`, {
                        id: item.id,
                        name: item.name,
                        type: item.type,
                        item_type: item.item_type,
                        source_type: item.source_type
                    });

                    if (item.item_type === 'file' || item.source_type === 'file_upload') {
                        console.log(`Found file: ${item.name}`);
                        const cleanName = item.name.replace(/^Home\//, '');
                        const filePath = parentPath ? `${parentPath}/${cleanName}` : cleanName;

                        allFiles.push({
                            ...item,
                            name: cleanName,
                            file_path: filePath
                        });
                    } else if (item.type === 'folder' || item.item_type === 'folder') {
                        if (item.name !== 'Home') {  // Skip processing Home folder again
                            console.log(`Found folder: ${item.name}, recursing...`);
                            const subFiles = await getAllFiles(item.id, item.name);
                            allFiles = allFiles.concat(subFiles);
                        }
                    }
                }

                return allFiles;
            };

            // First get the root listing
            console.log('Fetching root folder listing...');
            const rootRes = await fetch(`${API_ENDPOINTS.DATA_SOURCE.LIST}`, { headers });
            const rootData: APIListResponse<DataSourceItem> = await rootRes.json();
            console.log('Root response:', rootData.dataSources);

            let allFiles: DataSourceItem[] = [];

            // Find the Home folder
            const homeFolder = rootData.dataSources?.[0];
            console.log('First item in root response:', homeFolder);

            if (homeFolder?.name === 'Home') {
                console.log('Found Home folder with ID:', homeFolder.id);
                const files = await getAllFiles(homeFolder.id);
                console.log('Files found in Home folder:', files);
                allFiles = [...files];
            } else {
                console.warn('Could not find Home folder in:', rootData.dataSources);
            }

            console.log('Final list of all files found:', allFiles);

            // Fetch indexes and RAG applications
            const [indexesRes, ragRes] = await Promise.all([
                fetch(API_ENDPOINTS.INDEXES.LIST, { headers }),
                fetch(API_ENDPOINTS.RAG.LIST, { headers })
            ]);

            if (!indexesRes.ok || !ragRes.ok) {
                throw new Error('Failed to fetch indexes or RAG applications');
            }

            const indexes = await indexesRes.json();
            const ragApplications = await ragRes.json();

            const transformedData = {
                dataSources: allFiles,
                indexes: indexes || [],
                ragApplications: ragApplications || []
            };

            console.log('Final transformed data:', transformedData);
            setResourceData(transformedData);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch resource data');
            setResourceData({
                dataSources: [],
                indexes: [],
                ragApplications: []
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchAllData();
        }
    }, [user, fetchAllData]);

    if (!user) {
        return null;
    }

    return (
        <MainLayout
            user={user}
            modalProps={{
                profileData,
                error: modalState.error,
                success: modalState.success,
                isLoading: modalState.isLoading,
                showProfileModal: modalState.showProfileModal,
                handleProfileUpdate: (e) => profileManagement.handleProfileUpdate(e, profileData),
                handleDataChange: (data: Partial<ProfileData>) => {
                    if ('fullName' in data || 'email' in data) {
                        setProfileData(prev => ({ ...prev, ...data }));
                    }
                },
                setShowProfileModal: modalState.setShowProfileModal
            }}
        >
            <div className="container-fluid py-4">
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="fw-semibold">Resource Relationships</h5>
                            <p className="text-muted mb-0">
                                View relationships between data sources, knowledge bases, and RAG applications.
                            </p>
                        </div>
                        <button
                            onClick={fetchAllData}
                            className="btn btn-outline-secondary"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                'Refresh'
                            )}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <AlertCircle className="me-2" size={20} />
                        <div>{error}</div>
                    </div>
                )}

                <div className="row">
                    <div className="col-12">
                        <ResourceRelationships
                            data={resourceData}
                            isLoading={isLoading}
                            onRefresh={fetchAllData}
                        />
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ResourceRelationshipsPage;